import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, message, Popconfirm, Tag, Tabs, Row, Col, Switch, InputNumber, Divider, Space, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, SettingOutlined, TeamOutlined, GlobalOutlined, CloudOutlined, CheckCircleOutlined, StopOutlined, PlayCircleOutlined, TagOutlined, DatabaseOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { usersAPI, configAPI, statusAPI, contextAPI, clientsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Option } = Select;
const { TabPane } = Tabs;
const { TextArea } = Input;

const Settings = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [systemSettings, setSystemSettings] = useState({});
  const [storageConfigurations, setStorageConfigurations] = useState([]);
  const [statusSettings, setStatusSettings] = useState([]);
  const [contextSettings, setContextSettings] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isStorageModalOpen, setIsStorageModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingClient, setEditingClient] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [editingStorage, setEditingStorage] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);
  const [editingContext, setEditingContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(null);
  const [contextFilter, setContextFilter] = useState({ namespace: '', category: '' });
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [userForm] = Form.useForm();
  const [clientForm] = Form.useForm();
  const [configForm] = Form.useForm();
  const [systemForm] = Form.useForm();
  const [storageForm] = Form.useForm();
  const [statusForm] = Form.useForm();
  const [contextForm] = Form.useForm();

  const roles = [
    { value: 'super_admin', label: 'Super Admin', color: 'red' },
    { value: 'admin', label: 'Admin', color: 'orange' },
    { value: 'client', label: 'Client', color: 'blue' },
    { value: 'viewer', label: 'Viewer', color: 'green' },
  ];

  const defaultStatusSettings = [
    { key: 'working_on_it', label: 'Working on it', color: '#ffab00' },
    { key: 'estimation_needed', label: 'Estimation needed', color: '#579dff' },
    { key: 'discussion_needed', label: 'Discussion needed', color: '#00875a' },
    { key: 'blocked', label: 'Blocked', color: '#da282a' },
    { key: 'review', label: 'Review', color: '#8777d9' },
    { key: 'testing', label: 'Testing', color: '#943d73' },
    { key: 'priority', label: 'Priority', color: '#ff5733' },
    { key: 'waiting_for_approval', label: 'Waiting for approval', color: '#5e6c84' },
    { key: 'in_progress', label: 'In progress', color: '#0065ff' },
    { key: 'completed', label: 'Completed', color: '#36b37e' },
    { key: 'cancelled', label: 'Cancelled', color: '#6c757d' },
    { key: 'on_hold', label: 'On hold', color: '#ffc107' }
  ];

  const storageTypes = [
    { 
      value: 'aws_s3', 
      label: 'AWS S3', 
      icon: '🪣',
      fields: [
        { name: 'access_key', label: 'Access Key', type: 'input', required: true },
        { name: 'secret_key', label: 'Secret Key', type: 'password', required: true },
        { name: 'region', label: 'Region', type: 'input', required: true, placeholder: 'us-east-1' },
        { name: 'bucket', label: 'Bucket Name', type: 'input', required: true },
        { name: 'folder_prefix', label: 'Folder Prefix', type: 'input', required: false, placeholder: 'uploads/ or data/workflows/' }
      ]
    },
    { 
      value: 'custom_s3', 
      label: 'Custom S3 (MinIO, etc.)', 
      icon: '🗄️',
      fields: [
        { name: 'endpoint_url', label: 'Endpoint URL', type: 'input', required: true, placeholder: 'http://localhost:9000' },
        { name: 'access_key', label: 'Access Key', type: 'input', required: true },
        { name: 'secret_key', label: 'Secret Key', type: 'password', required: true },
        { name: 'bucket', label: 'Bucket Name', type: 'input', required: true },
        { name: 'folder_prefix', label: 'Folder Prefix', type: 'input', required: false, placeholder: 'uploads/ or data/workflows/' },
        { name: 'ssl_verify', label: 'SSL Verify', type: 'switch', required: false, defaultValue: true }
      ]
    },
    { 
      value: 'dropbox', 
      label: 'Dropbox', 
      icon: '📦',
      fields: [
        { name: 'access_token', label: 'Access Token', type: 'password', required: true }
      ]
    },
    { 
      value: 'sftp', 
      label: 'SFTP', 
      icon: '🔒',
      fields: [
        { name: 'host', label: 'Host', type: 'input', required: true },
        { name: 'port', label: 'Port', type: 'number', required: true, defaultValue: 22 },
        { name: 'username', label: 'Username', type: 'input', required: true },
        { name: 'password', label: 'Password', type: 'password', required: false },
        { name: 'key_file', label: 'SSH Key File Path', type: 'input', required: false },
        { name: 'remote_path', label: 'Remote Path', type: 'input', required: true, placeholder: '/uploads' }
      ]
    },
    { 
      value: 'ftp', 
      label: 'FTP', 
      icon: '📁',
      fields: [
        { name: 'host', label: 'Host', type: 'input', required: true },
        { name: 'port', label: 'Port', type: 'number', required: true, defaultValue: 21 },
        { name: 'username', label: 'Username', type: 'input', required: true },
        { name: 'password', label: 'Password', type: 'password', required: true },
        { name: 'remote_path', label: 'Remote Path', type: 'input', required: true, placeholder: '/uploads' },
        { name: 'passive_mode', label: 'Passive Mode', type: 'switch', required: false, defaultValue: true }
      ]
    },
    { 
      value: 'webdav', 
      label: 'WebDAV', 
      icon: '🌐',
      fields: [
        { name: 'base_url', label: 'WebDAV URL', type: 'input', required: true, placeholder: 'http://localhost:8080/' },
        { name: 'username', label: 'Username', type: 'input', required: true },
        { name: 'password', label: 'Password', type: 'password', required: true },
        { name: 'remote_path', label: 'Remote Path', type: 'input', required: false, placeholder: '/uploads' },
        { name: 'timeout', label: 'Timeout (seconds)', type: 'number', required: false, defaultValue: 30 },
        { name: 'ssl_verify', label: 'SSL Verify', type: 'switch', required: false, defaultValue: true }
      ]
    }
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchUsers();
    fetchClients();
    if (user?.role === 'super_admin') {
      fetchConfigurations();
      fetchSystemSettings();
      fetchStorageConfigurations();
      fetchStatusSettings();
      fetchContextSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Handle URL parameters for tab selection
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && ['users', 'clients', 'config', 'storage', 'status', 'context'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Window resize handler for responsive table heights
  const handleResize = useCallback(() => {
    setWindowHeight(window.innerHeight);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // Calculate table height based on window size and content
  const calculateTableHeight = (baseOffset = 400) => {
    return Math.max(300, windowHeight - baseOffset);
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      message.error('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getClients();
      setClients(response.data.clients || []);
    } catch (error) {
      message.error('Failed to fetch clients');
      console.error('Error fetching clients:', error);
    }
  };

  const fetchConfigurations = async () => {
    try {
      const response = await configAPI.getConfigurations();
      setConfigurations(response.data || []);
    } catch (error) {
      console.error('Error fetching configurations:', error);
      setConfigurations([]);
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const response = await configAPI.getSystemSettings();
      setSystemSettings(response.data || {});
      systemForm.setFieldsValue(response.data || {});
    } catch (error) {
      console.error('Error fetching system settings:', error);
    }
  };

  const fetchStorageConfigurations = async () => {
    try {
      const response = await configAPI.getStorageConfigurations();
      setStorageConfigurations(response.data || []);
    } catch (error) {
      console.error('Error fetching storage configurations:', error);
      setStorageConfigurations([]);
    }
  };

  const fetchStatusSettings = async () => {
    try {
      const response = await statusAPI.getStatusSettings();
      setStatusSettings(response.data || defaultStatusSettings);
    } catch (error) {
      console.error('Error fetching status settings:', error);
      setStatusSettings(defaultStatusSettings);
    }
  };

  const fetchContextSettings = async () => {
    try {
      const response = await contextAPI.getGlobalContext(contextFilter);
      setContextSettings(response.data || []);
    } catch (error) {
      console.error('Error fetching context settings:', error);
      setContextSettings([]);
    }
  };

  const openUserModal = (user = null) => {
    setEditingUser(user);
    if (user) {
      userForm.setFieldsValue({
        username: user.username,
        email: user.email,
        role: user.role,
        client_ids: user.clients ? user.clients.map(client => client.id) : [],
      });
    } else {
      userForm.resetFields();
    }
    setIsUserModalOpen(true);
  };

  const openConfigModal = (config = null) => {
    setEditingConfig(config);
    if (config) {
      configForm.setFieldsValue({
        key: config.key,
        value: config.value,
        description: config.description,
        category: config.category,
      });
    } else {
      configForm.resetFields();
    }
    setIsConfigModalOpen(true);
  };

  const openStorageModal = (storage = null) => {
    setEditingStorage(storage);
    if (storage) {
      const formValues = {
        name: storage.name,
        storage_type: storage.storage_type,
        is_active: storage.is_active,
        is_default: storage.is_default,
        description: storage.description,
        ...storage.connection_config
      };
      storageForm.setFieldsValue(formValues);
    } else {
      storageForm.resetFields();
    }
    setIsStorageModalOpen(true);
  };

  const openStatusModal = (status = null) => {
    setEditingStatus(status);
    if (status) {
      statusForm.setFieldsValue({
        key: status.key,
        label: status.label,
        color: status.color,
      });
    } else {
      statusForm.resetFields();
    }
    setIsStatusModalOpen(true);
  };

  const openContextModal = (context = null) => {
    setEditingContext(context);
    if (context) {
      // Extract namespace type and client ID from existing context
      let namespaceType = 'custom';
      let clientId = null;
      let customNamespace = context.namespace;
      
      if (context.namespace.startsWith('client.')) {
        namespaceType = 'client';
        clientId = parseInt(context.namespace.split('.')[1]);
      } else if (context.namespace === 'system') {
        namespaceType = 'system';
      } else if (context.namespace.startsWith('workflow.')) {
        namespaceType = 'workflow';
      }
      
      contextForm.setFieldsValue({
        namespace_type: namespaceType,
        client_id: clientId,
        custom_namespace: namespaceType === 'custom' ? customNamespace : '',
        namespace: context.namespace,
        context_key: context.context_key,
        context_value: context.context_value,
        description: context.description,
        category: context.category,
      });
    } else {
      contextForm.resetFields();
      // Set default values for new context
      contextForm.setFieldsValue({
        namespace_type: 'client',
        category: 'General'
      });
    }
    setIsContextModalOpen(true);
  };

  const handleUserSave = async (values) => {
    try {
      setLoading(true);
      if (editingUser) {
        await usersAPI.updateUser(editingUser.id, {
          username: values.username,
          email: values.email,
          role: values.role,
          client_ids: values.client_ids || [],
          ...(values.password && { password: values.password })
        });
        message.success('User updated successfully');
      } else {
        await usersAPI.createUser({
          username: values.username,
          email: values.email,
          password: values.password,
          role: values.role,
          client_ids: values.client_ids || []
        });
        message.success('User created successfully');
      }

      setIsUserModalOpen(false);
      userForm.resetFields();
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to save user');
      console.error('Error saving user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigSave = async (values) => {
    try {
      setLoading(true);
      if (editingConfig) {
        await configAPI.updateConfiguration(editingConfig.id, values);
        message.success('Configuration updated successfully');
      } else {
        await configAPI.createConfiguration(values);
        message.success('Configuration created successfully');
      }

      setIsConfigModalOpen(false);
      configForm.resetFields();
      setEditingConfig(null);
      fetchConfigurations();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to save configuration');
      console.error('Error saving configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSystemSettingsSave = async (values) => {
    try {
      setLoading(true);
      await configAPI.updateSystemSettings(values);
      message.success('System settings updated successfully');
      setSystemSettings(values);
    } catch (error) {
      message.error('Failed to update system settings');
      console.error('Error updating system settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStorageSave = async (values) => {
    try {
      setLoading(true);
      
      // Separate connection config from other form values
      const { name, storage_type, is_active, is_default, description, ...connectionConfig } = values;
      
      const storageData = {
        name,
        storage_type,
        is_active: is_active ?? true,
        is_default: is_default ?? false,
        description: description || '',
        connection_config: connectionConfig
      };

      if (editingStorage) {
        await configAPI.updateStorageConfiguration(editingStorage.id, storageData);
        message.success('Storage configuration updated successfully');
      } else {
        await configAPI.createStorageConfiguration(storageData);
        message.success('Storage configuration created successfully');
      }

      setIsStorageModalOpen(false);
      storageForm.resetFields();
      setEditingStorage(null);
      fetchStorageConfigurations();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to save storage configuration');
      console.error('Error saving storage configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusSave = async (values) => {
    try {
      setLoading(true);
      
      if (editingStatus) {
        // Update existing status
        await statusAPI.updateStatusSetting(editingStatus.key, values);
        message.success('Status updated successfully');
      } else {
        // Add new status
        await statusAPI.createStatusSetting(values);
        message.success('Status created successfully');
      }

      // Refresh status settings
      await fetchStatusSettings();
      setIsStatusModalOpen(false);
      statusForm.resetFields();
      setEditingStatus(null);
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to save status');
      console.error('Error saving status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContextSave = async (values) => {
    try {
      setLoading(true);
      
      if (editingContext) {
        // Update existing context
        await contextAPI.updateContextEntry(editingContext.id, values);
        message.success('Context entry updated successfully');
      } else {
        // Add new context
        await contextAPI.createContextEntry(values);
        message.success('Context entry created successfully');
      }

      // Refresh context settings
      await fetchContextSettings();
      setIsContextModalOpen(false);
      contextForm.resetFields();
      setEditingContext(null);
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to save context entry');
      console.error('Error saving context:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    try {
      setLoading(true);
      await usersAPI.deleteUser(userId);
      message.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to delete user');
      console.error('Error deleting user:', error);
    } finally {
      setLoading(false);
    }
  };

  // Client management functions
  const openClientModal = (client = null) => {
    setEditingClient(client);
    if (client) {
      clientForm.setFieldsValue({
        name: client.name,
        description: client.description,
        contact_email: client.contact_email,
        contact_phone: client.contact_phone,
      });
    } else {
      clientForm.resetFields();
    }
    setIsClientModalOpen(true);
  };

  const handleClientSave = async (values) => {
    try {
      setLoading(true);
      if (editingClient) {
        // Update existing client
        await clientsAPI.updateClient(editingClient.id, values);
        message.success('Client updated successfully');
      } else {
        // Create new client
        await clientsAPI.createClient(values);
        message.success('Client created successfully');
      }
      setIsClientModalOpen(false);
      setEditingClient(null);
      clientForm.resetFields();
      fetchClients();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to save client');
      console.error('Error saving client:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteClient = async (clientId) => {
    try {
      setLoading(true);
      await clientsAPI.deleteClient(clientId);
      message.success('Client deleted successfully');
      fetchClients();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to delete client');
      console.error('Error deleting client:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteConfiguration = async (configId) => {
    try {
      setLoading(true);
      await configAPI.deleteConfiguration(configId);
      message.success('Configuration deleted successfully');
      fetchConfigurations();
    } catch (error) {
      message.error('Failed to delete configuration');
      console.error('Error deleting configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteStorageConfiguration = async (storageId) => {
    try {
      setLoading(true);
      await configAPI.deleteStorageConfiguration(storageId);
      message.success('Storage configuration deleted successfully');
      fetchStorageConfigurations();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to delete storage configuration');
      console.error('Error deleting storage configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteStatus = async (statusKey) => {
    try {
      setLoading(true);
      await statusAPI.deleteStatusSetting(statusKey);
      await fetchStatusSettings();
      message.success('Status deleted successfully');
    } catch (error) {
      message.error('Failed to delete status');
      console.error('Error deleting status:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteContext = async (contextId) => {
    try {
      setLoading(true);
      await contextAPI.deleteContextEntry(contextId);
      await fetchContextSettings();
      message.success('Context entry deleted successfully');
    } catch (error) {
      message.error('Failed to delete context entry');
      console.error('Error deleting context:', error);
    } finally {
      setLoading(false);
    }
  };

  const testStorageConnection = async (storageId) => {
    try {
      setTestingConnection(storageId);
      const response = await configAPI.testStorageConfiguration(storageId);
      if (response.data.success) {
        message.success(response.data.message);
      } else {
        message.error(response.data.message);
      }
    } catch (error) {
      message.error('Connection test failed');
      console.error('Error testing storage connection:', error);
    } finally {
      setTestingConnection(null);
    }
  };

  const getRoleColor = (role) => {
    const roleConfig = roles.find(r => r.value === role);
    return roleConfig ? roleConfig.color : 'default';
  };

  const userColumns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      render: (text) => (
        <span>
          <UserOutlined style={{ marginRight: 8 }} />
          {text}
        </span>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={getRoleColor(role)}>
          {roles.find(r => r.value === role)?.label || role}
        </Tag>
      ),
    },
    {
      title: 'Client',
      dataIndex: 'clients',
      key: 'clients',
      render: (clients) => (
        <span>
          {clients && clients.length > 0 ? (
            clients.map(client => (
              <Tag key={client.id} color="blue" style={{ marginBottom: 4 }}>
                {client.name}
              </Tag>
            ))
          ) : (
            <Tag color="default">No Clients</Tag>
          )}
        </span>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openUserModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this user?"
            description="This action cannot be undone."
            onConfirm={() => deleteUser(record.id)}
          >
            <Button size="small" icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const clientColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <span>
          <TeamOutlined style={{ marginRight: 8 }} />
          {text}
        </span>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Contact Email',
      dataIndex: 'contact_email',
      key: 'contact_email',
    },
    {
      title: 'Contact Phone',
      dataIndex: 'contact_phone',
      key: 'contact_phone',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openClientModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this client?"
            description="This will also delete all their tasks and workflows."
            onConfirm={() => deleteClient(record.id)}
          >
            <Button size="small" icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const configColumns = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (text) => <code>{text}</code>,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (text) => <span style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => <Tag>{category || 'General'}</Tag>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openConfigModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this configuration?"
            onConfirm={() => deleteConfiguration(record.id)}
          >
            <Button size="small" icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const storageColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span>
          <span style={{ marginRight: 8, fontSize: 16 }}>
            {storageTypes.find(t => t.value === record.storage_type)?.icon || '🗄️'}
          </span>
          {text}
          {record.is_default && (
            <Tag color="blue" style={{ marginLeft: 8 }}>Default</Tag>
          )}
        </span>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'storage_type',
      key: 'storage_type',
      render: (type) => {
        const storageType = storageTypes.find(t => t.value === type);
        return (
          <Tag color="geekblue">
            {storageType?.label || type}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'} icon={isActive ? <CheckCircleOutlined /> : <StopOutlined />}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Test Connection">
            <Button
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => testStorageConnection(record.id)}
              loading={testingConnection === record.id}
              disabled={!record.is_active}
            >
              Test
            </Button>
          </Tooltip>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openStorageModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this storage configuration?"
            description="This action cannot be undone."
            onConfirm={() => deleteStorageConfiguration(record.id)}
          >
            <Button size="small" icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const statusColumns = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      width: 120,
    },
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      width: 100,
      render: (color, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '20px',
              height: '20px',
              backgroundColor: color,
              borderRadius: '4px',
              border: '1px solid #d9d9d9',
            }}
          />
          <Tag color={color}>{record.label}</Tag>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openStatusModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this status?"
            description="This action cannot be undone."
            onConfirm={() => deleteStatus(record.key)}
          >
            <Button size="small" icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const contextColumns = [
    {
      title: 'Namespace',
      dataIndex: 'namespace',
      key: 'namespace',
      width: 140,
      fixed: 'left',
      render: (namespace) => <Tag color="blue">{namespace}</Tag>,
      sorter: (a, b) => a.namespace.localeCompare(b.namespace),
    },
    {
      title: 'Key',
      dataIndex: 'context_key',
      key: 'context_key',
      width: 130,
      render: (key) => <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>{key}</code>,
      sorter: (a, b) => a.context_key.localeCompare(b.context_key),
    },
    {
      title: 'Value',
      dataIndex: 'context_value',
      key: 'context_value',
      width: 180,
      ellipsis: { showTitle: true },
      render: (value) => (
        <span 
          style={{ 
            maxWidth: 160, 
            overflow: 'hidden', 
            textOverflow: 'ellipsis',
            display: 'block'
          }}
          title={value}
        >
          {value}
        </span>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 90,
      render: (category) => <Tag>{category || 'General'}</Tag>,
      filters: [
        { text: 'General', value: 'General' },
        { text: 'Workflow', value: 'Workflow' },
        { text: 'Storage', value: 'Storage' },
        { text: 'Notifications', value: 'Notifications' },
      ],
      onFilter: (value, record) => (record.category || 'General') === value,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: { showTitle: true },
      render: (description) => (
        <span title={description}>{description}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openContextModal(record)}
            title="Edit context entry"
          />
          <Popconfirm
            title="Delete this context entry?"
            description="This action cannot be undone."
            onConfirm={() => deleteContext(record.id)}
          >
            <Button 
              size="small" 
              icon={<DeleteOutlined />} 
              danger
              title="Delete context entry"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Filter context settings based on selected filters
  const filteredContextSettings = contextSettings.filter(context => {
    if (contextFilter.namespace && context.namespace !== contextFilter.namespace) {
      return false;
    }
    if (contextFilter.category && context.category !== contextFilter.category) {
      return false;
    }
    return true;
  });

  // Filter global context settings (exclude client-specific contexts)
  const filteredGlobalContextSettings = filteredContextSettings.filter(context => {
    // Only show system and workflow template contexts, not client-specific ones
    return !context.namespace.startsWith('client.');
  });

  return (
    <div>
      <Tabs activeKey={activeTab} onChange={setActiveTab} type="card">
        <TabPane tab={<span><UserOutlined />All Users</span>} key="users">
          <Card
            title="User Management"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openUserModal()}
              >
                Add User
              </Button>
            }
          >
            <Table
              columns={userColumns}
              dataSource={users}
              rowKey="id"
              loading={loading}
              scroll={{ y: calculateTableHeight(450) }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} users`,
              }}
            />
          </Card>

          <Card title="Role Information" style={{ marginTop: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              {roles.map(role => (
                <div key={role.value} style={{ padding: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
                  <Tag color={role.color} style={{ marginBottom: 8 }}>{role.label}</Tag>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {role.value === 'super_admin' && 'Full system access, can manage all users and settings'}
                    {role.value === 'admin' && 'Can manage users, workflows, and view all data'}
                    {role.value === 'client' && 'Can create and manage their own workflows'}
                    {role.value === 'viewer' && 'Read-only access to assigned workflows'}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabPane>

        <TabPane tab={<span><TeamOutlined />Clients</span>} key="clients">
          <Card
            title="Client Management"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openClientModal()}
              >
                Add Client
              </Button>
            }
          >
            <Table
              columns={clientColumns}
              dataSource={clients}
              rowKey="id"
              loading={loading}
              scroll={{ y: calculateTableHeight(450) }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `Total ${total} clients`,
              }}
              expandable={{
                expandedRowRender: (client) => {
                  const clientContextSettings = contextSettings.filter(context => 
                    context.namespace === `client.${client.id}`
                  );

                  const clientContextColumns = [
                    {
                      title: 'Key',
                      dataIndex: 'context_key',
                      key: 'context_key',
                      width: 130,
                      render: (key) => <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>{key}</code>,
                    },
                    {
                      title: 'Value',
                      dataIndex: 'context_value',
                      key: 'context_value',
                      width: 180,
                      ellipsis: { showTitle: true },
                      render: (value) => (
                        <span 
                          style={{ 
                            maxWidth: 160, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            display: 'block'
                          }}
                          title={value}
                        >
                          {value}
                        </span>
                      ),
                    },
                    {
                      title: 'Category',
                      dataIndex: 'category',
                      key: 'category',
                      width: 90,
                      render: (category) => <Tag>{category || 'General'}</Tag>,
                    },
                    {
                      title: 'Description',
                      dataIndex: 'description',
                      key: 'description',
                      ellipsis: { showTitle: true },
                      render: (description) => (
                        <span title={description}>{description}</span>
                      ),
                    },
                    {
                      title: 'Actions',
                      key: 'actions',
                      width: 120,
                      render: (_, record) => (
                        <Space>
                          <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openContextModal(record)}
                            title="Edit context entry"
                          />
                          <Popconfirm
                            title="Delete this context entry?"
                            description="This action cannot be undone."
                            onConfirm={() => deleteContext(record.id)}
                          >
                            <Button 
                              size="small" 
                              icon={<DeleteOutlined />} 
                              danger
                              title="Delete context entry"
                            />
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ];

                  return (
                    <div style={{ padding: '16px', backgroundColor: '#f9f9f9' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h4 style={{ margin: 0, color: '#1890ff' }}>
                          <DatabaseOutlined style={{ marginRight: 8 }} />
                          Context Settings for {client.name}
                        </h4>
                        <Space>
                          <Button
                            size="small"
                            onClick={() => {
                              contextForm.resetFields();
                              contextForm.setFieldsValue({
                                namespace_type: 'client',
                                client_id: client.id,
                                namespace: `client.${client.id}`,
                                context_key: 'jobprefix',
                                context_value: `${client.name.toLowerCase().replace(/\s+/g, '_')}_`,
                                category: 'Workflow',
                                description: 'Default prefix for job names and file naming'
                              });
                              setIsContextModalOpen(true);
                            }}
                          >
                            + Add Job Prefix
                          </Button>
                          <Button
                            type="primary"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              contextForm.resetFields();
                              contextForm.setFieldsValue({
                                namespace_type: 'client',
                                client_id: client.id,
                                namespace: `client.${client.id}`,
                                category: 'General'
                              });
                              setIsContextModalOpen(true);
                            }}
                          >
                            Add Context
                          </Button>
                        </Space>
                      </div>
                      {clientContextSettings.length > 0 ? (
                        <Table
                          columns={clientContextColumns}
                          dataSource={clientContextSettings}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          scroll={{ x: 800 }}
                        />
                      ) : (
                        <div style={{ 
                          textAlign: 'center', 
                          padding: '20px', 
                          color: '#999',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px dashed #d9d9d9'
                        }}>
                          No context settings found for this client.
                          <br />
                          <Button 
                            type="link" 
                            onClick={() => {
                              contextForm.resetFields();
                              contextForm.setFieldsValue({
                                namespace_type: 'client',
                                client_id: client.id,
                                namespace: `client.${client.id}`,
                                context_key: 'jobprefix',
                                context_value: `${client.name.toLowerCase().replace(/\s+/g, '_')}_`,
                                category: 'Workflow',
                                description: 'Default prefix for job names and file naming'
                              });
                              setIsContextModalOpen(true);
                            }}
                          >
                            Add your first context setting
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                },
                rowExpandable: () => true,
              }}
            />
          </Card>
        </TabPane>

        {user?.role === 'super_admin' && (
          <TabPane tab={<span><GlobalOutlined />Global Config</span>} key="config">
            <Row gutter={16}>
              <Col span={12}>
                <Card
                  title="System Settings"
                  extra={
                    <Button
                      type="primary"
                      icon={<SettingOutlined />}
                      onClick={() => systemForm.submit()}
                      loading={loading}
                    >
                      Save Settings
                    </Button>
                  }
                >
                  <Form
                    form={systemForm}
                    layout="vertical"
                    onFinish={handleSystemSettingsSave}
                  >
                    <Form.Item
                      name="max_workflows_per_client"
                      label="Max Workflows per Client"
                      initialValue={10}
                    >
                      <InputNumber min={1} max={1000} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      name="enable_user_registration"
                      label="Enable User Registration"
                      valuePropName="checked"
                      initialValue={false}
                    >
                      <Switch />
                    </Form.Item>

                    <Form.Item
                      name="session_timeout_minutes"
                      label="Session Timeout (minutes)"
                      initialValue={60}
                    >
                      <InputNumber min={5} max={1440} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                      name="enable_email_notifications"
                      label="Enable Email Notifications"
                      valuePropName="checked"
                      initialValue={true}
                    >
                      <Switch />
                    </Form.Item>

                    <Form.Item
                      name="max_file_upload_size_mb"
                      label="Max File Upload Size (MB)"
                      initialValue={10}
                    >
                      <InputNumber min={1} max={100} style={{ width: '100%' }} />
                    </Form.Item>
                  </Form>
                </Card>
              </Col>

              <Col span={12}>
                <Card
                  title="Custom Configurations"
                  extra={
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => openConfigModal()}
                    >
                      Add Config
                    </Button>
                  }
                >
                  <Table
                    columns={configColumns}
                    dataSource={configurations}
                    rowKey="id"
                    loading={loading}
                    size="small"
                    pagination={{
                      pageSize: 5,
                      showTotal: (total) => `${total} configs`,
                    }}
                  />
                </Card>
              </Col>
            </Row>
          </TabPane>
        )}

        {user?.role === 'super_admin' && (
          <TabPane tab={<span><CloudOutlined />Storage Services</span>} key="storage">
            <Card
              title="Storage Service Configurations"
              extra={
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => openStorageModal()}
                >
                  Add Storage Service
                </Button>
              }
            >
              <Table
                columns={storageColumns}
                dataSource={storageConfigurations}
                rowKey="id"
                loading={loading}
                scroll={{ y: calculateTableHeight(450) }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total ${total} storage configurations`,
                }}
              />
            </Card>

            <Card title="Storage Service Types" style={{ marginTop: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                {storageTypes.map(type => (
                  <div key={type.value} style={{ padding: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 20, marginRight: 8 }}>{type.icon}</span>
                      <Tag color="geekblue">{type.label}</Tag>
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {type.value === 'aws_s3' && 'Amazon Web Services S3 bucket storage'}
                      {type.value === 'custom_s3' && 'S3-compatible storage like MinIO, DigitalOcean Spaces'}
                      {type.value === 'dropbox' && 'Dropbox cloud storage integration'}
                      {type.value === 'sftp' && 'Secure File Transfer Protocol (SSH-based)'}
                      {type.value === 'ftp' && 'File Transfer Protocol (traditional FTP)'}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabPane>
        )}

        <TabPane tab={<span><TagOutlined />Status Settings</span>} key="status">
          <Card
            title="Status Management"
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openStatusModal()}
              >
                Add Status
              </Button>
            }
          >
            <Table
              columns={statusColumns}
              dataSource={statusSettings}
              rowKey="key"
              pagination={{ pageSize: 10 }}
              scroll={{ y: calculateTableHeight(450) }}
              size="middle"
            />
          </Card>
        </TabPane>

        {(user?.role === 'super_admin' || user?.role === 'admin') && (
          <TabPane tab={<span><DatabaseOutlined />Global Context</span>} key="context">
            <Card
              title="System & Workflow Context Management"
              extra={
                <Space>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => openContextModal()}
                  >
                    Add Context
                  </Button>
                </Space>
              }
              bodyStyle={{ padding: '16px 0' }}
            >
              <div style={{ padding: '0 24px', marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Space wrap>
                      <Select
                        placeholder="Filter by namespace"
                        allowClear
                        style={{ width: 200 }}
                        onChange={(value) => setContextFilter({...contextFilter, namespace: value})}
                      >
                        <Option value="">All Namespaces</Option>
                        <Option value="system">System</Option>
                        <Option value="workflow.templates">Workflow Templates</Option>
                      </Select>
                      <Select
                        placeholder="Filter by category"
                        allowClear
                        style={{ width: 150 }}
                        onChange={(value) => setContextFilter({...contextFilter, category: value})}
                      >
                        <Option value="">All Categories</Option>
                        <Option value="General">General</Option>
                        <Option value="Workflow">Workflow</Option>
                        <Option value="Storage">Storage</Option>
                        <Option value="Notifications">Notifications</Option>
                        <Option value="Performance">Performance</Option>
                        <Option value="Templates">Templates</Option>
                      </Select>
                    </Space>
                  </Col>
                  <Col span={24}>
                    <div style={{ backgroundColor: '#e6f7ff', padding: '12px', borderRadius: '6px', border: '1px solid #91d5ff' }}>
                      <div style={{ marginBottom: '8px', fontWeight: 'bold', color: '#1890ff' }}>
                        ℹ️ Note: Client-specific context settings are now managed under each client in the Clients tab.
                      </div>
                      <div style={{ color: '#666', fontSize: '14px' }}>
                        This section is for system-wide and workflow template contexts only.
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
              <div style={{ 
                width: '100%', 
                overflowX: 'auto',
                overflowY: 'hidden',
                border: '1px solid #f0f0f0',
                borderRadius: '6px'
              }}>
                <Table
                  columns={contextColumns}
                  dataSource={filteredGlobalContextSettings}
                  rowKey="id"
                  pagination={{ 
                    pageSize: 10,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                  }}
                  size="middle"
                  scroll={{ 
                    x: 1000,
                    y: calculateTableHeight(450) 
                  }}
                  style={{ minWidth: '1000px' }}
                />
              </div>
            </Card>
          </TabPane>
        )}
      </Tabs>

      {/* User Modal */}
      <Modal
        title={editingUser ? 'Edit User' : 'Add New User'}
        open={isUserModalOpen}
        onCancel={() => setIsUserModalOpen(false)}
        footer={null}
      >
        <Form
          form={userForm}
          layout="vertical"
          onFinish={handleUserSave}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Please enter username' },
              { min: 3, message: 'Username must be at least 3 characters' },
            ]}
          >
            <Input placeholder="Enter username" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>

          <Form.Item
            name="password"
            label={editingUser ? "New Password (leave empty to keep current)" : "Password"}
            rules={editingUser ? [] : [
              { required: true, message: 'Please enter password' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password placeholder={editingUser ? "Enter new password (optional)" : "Enter password"} />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder="Select user role">
              {roles.map(role => (
                <Option key={role.value} value={role.value}>
                  <Tag color={role.color}>{role.label}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="client_ids"
            label="Clients"
            rules={[{ required: false, message: 'Please select clients' }]}
          >
            <Select 
              mode="multiple" 
              placeholder="Select clients (optional)" 
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {clients.map(client => (
                <Option key={client.id} value={client.id}>
                  {client.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'end', gap: 8, marginTop: 24 }}>
            <Button onClick={() => setIsUserModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Client Modal */}
      <Modal
        title={editingClient ? 'Edit Client' : 'Add New Client'}
        open={isClientModalOpen}
        onCancel={() => setIsClientModalOpen(false)}
        footer={null}
      >
        <Form
          form={clientForm}
          layout="vertical"
          onFinish={handleClientSave}
        >
          <Form.Item
            name="name"
            label="Client Name"
            rules={[
              { required: true, message: 'Please enter client name' },
              { min: 2, message: 'Client name must be at least 2 characters' },
            ]}
          >
            <Input placeholder="Enter client name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ max: 500, message: 'Description cannot exceed 500 characters' }]}
          >
            <TextArea 
              rows={3} 
              placeholder="Enter client description (optional)" 
              showCount 
              maxLength={500} 
            />
          </Form.Item>

          <Form.Item
            name="contact_email"
            label="Contact Email"
            rules={[
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="Enter contact email (optional)" />
          </Form.Item>

          <Form.Item
            name="contact_phone"
            label="Contact Phone"
            rules={[{ max: 20, message: 'Phone number cannot exceed 20 characters' }]}
          >
            <Input placeholder="Enter contact phone (optional)" />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'end', gap: 8, marginTop: 24 }}>
            <Button onClick={() => setIsClientModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingClient ? 'Update' : 'Create'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Configuration Modal */}
      <Modal
        title={editingConfig ? 'Edit Configuration' : 'Add Configuration'}
        open={isConfigModalOpen}
        onCancel={() => setIsConfigModalOpen(false)}
        footer={null}
      >
        <Form
          form={configForm}
          layout="vertical"
          onFinish={handleConfigSave}
        >
          <Form.Item
            name="key"
            label="Configuration Key"
            rules={[
              { required: true, message: 'Please enter configuration key' },
              { pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/, message: 'Key must be alphanumeric with underscores' },
            ]}
          >
            <Input placeholder="e.g., api_rate_limit" disabled={!!editingConfig} />
          </Form.Item>

          <Form.Item
            name="value"
            label="Value"
            rules={[{ required: true, message: 'Please enter value' }]}
          >
            <TextArea rows={3} placeholder="Configuration value" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
          >
            <Select placeholder="Select category" allowClear>
              <Option value="API">API</Option>
              <Option value="Security">Security</Option>
              <Option value="Performance">Performance</Option>
              <Option value="Integration">Integration</Option>
              <Option value="General">General</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={2} placeholder="Brief description of this configuration" />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'end', gap: 8, marginTop: 24 }}>
            <Button onClick={() => setIsConfigModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingConfig ? 'Update' : 'Create'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Storage Configuration Modal */}
      <Modal
        title={editingStorage ? 'Edit Storage Configuration' : 'Add Storage Configuration'}
        open={isStorageModalOpen}
        onCancel={() => setIsStorageModalOpen(false)}
        footer={null}
        width={600}
      >
        <Form
          form={storageForm}
          layout="vertical"
          onFinish={handleStorageSave}
        >
          <Form.Item
            name="name"
            label="Configuration Name"
            rules={[
              { required: true, message: 'Please enter a configuration name' },
              { min: 3, message: 'Name must be at least 3 characters' },
            ]}
          >
            <Input placeholder="e.g., Production S3, Development MinIO" />
          </Form.Item>

          <Form.Item
            name="storage_type"
            label="Storage Type"
            rules={[{ required: true, message: 'Please select a storage type' }]}
          >
            <Select 
              placeholder="Select storage service type"
              onChange={() => storageForm.resetFields(['connection_config'])}
            >
              {storageTypes.map(type => (
                <Option key={type.value} value={type.value}>
                  <span style={{ marginRight: 8 }}>{type.icon}</span>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="Active"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_default"
                label="Set as Default"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={2} placeholder="Brief description of this storage configuration" />
          </Form.Item>

          <Divider>Connection Settings</Divider>

          <Form.Item dependencies={['storage_type']} noStyle>
            {({ getFieldValue }) => {
              const selectedType = getFieldValue('storage_type');
              const typeConfig = storageTypes.find(t => t.value === selectedType);
              
              if (!typeConfig) {
                return <div style={{ color: '#999', fontStyle: 'italic' }}>Select a storage type to configure connection settings</div>;
              }

              return (
                <>
                  {typeConfig.fields.map(field => {
                    const commonProps = {
                      name: field.name,
                      label: field.label,
                      rules: field.required ? [{ required: true, message: `Please enter ${field.label.toLowerCase()}` }] : []
                    };

                    switch (field.type) {
                      case 'password':
                        return (
                          <Form.Item key={field.name} {...commonProps}>
                            <Input.Password placeholder={field.placeholder} />
                          </Form.Item>
                        );
                      case 'number':
                        return (
                          <Form.Item key={field.name} {...commonProps} initialValue={field.defaultValue}>
                            <InputNumber style={{ width: '100%' }} placeholder={field.placeholder} />
                          </Form.Item>
                        );
                      case 'switch':
                        return (
                          <Form.Item 
                            key={field.name} 
                            {...commonProps} 
                            valuePropName="checked"
                            initialValue={field.defaultValue}
                          >
                            <Switch />
                          </Form.Item>
                        );
                      default:
                        return (
                          <Form.Item key={field.name} {...commonProps}>
                            <Input placeholder={field.placeholder} />
                          </Form.Item>
                        );
                    }
                  })}
                </>
              );
            }}
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'end', gap: 8, marginTop: 24 }}>
            <Button onClick={() => setIsStorageModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingStorage ? 'Update' : 'Create'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Status Modal */}
      <Modal
        title={editingStatus ? 'Edit Status' : 'Add New Status'}
        open={isStatusModalOpen}
        onCancel={() => setIsStatusModalOpen(false)}
        footer={null}
      >
        <Form
          form={statusForm}
          onFinish={handleStatusSave}
          layout="vertical"
        >
          <Form.Item
            name="key"
            label="Status Key"
            rules={[{ required: true, message: 'Please enter status key' }]}
          >
            <Input 
              placeholder="e.g., working_on_it, completed, blocked"
              disabled={editingStatus}
            />
          </Form.Item>

          <Form.Item
            name="label"
            label="Display Label"
            rules={[{ required: true, message: 'Please enter status label' }]}
          >
            <Input placeholder="e.g., Working on it, Completed, Blocked" />
          </Form.Item>

          <Form.Item
            name="color"
            label="Color"
            rules={[{ required: true, message: 'Please select a color' }]}
          >
            <Select
              placeholder="Select a color"
              dropdownRender={(menu) => (
                <div>
                  {menu}
                  <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                    <Input
                      placeholder="Custom hex color (e.g., #ff5733)"
                      onChange={(e) => {
                        if (e.target.value.match(/^#[0-9A-F]{6}$/i)) {
                          statusForm.setFieldsValue({ color: e.target.value });
                        }
                      }}
                    />
                  </div>
                </div>
              )}
            >
              <Option value="#ffab00">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: '#ffab00', borderRadius: '2px' }} />
                  Orange
                </div>
              </Option>
              <Option value="#579dff">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: '#579dff', borderRadius: '2px' }} />
                  Blue
                </div>
              </Option>
              <Option value="#00875a">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: '#00875a', borderRadius: '2px' }} />
                  Green
                </div>
              </Option>
              <Option value="#da282a">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: '#da282a', borderRadius: '2px' }} />
                  Red
                </div>
              </Option>
              <Option value="#8777d9">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: '#8777d9', borderRadius: '2px' }} />
                  Purple
                </div>
              </Option>
              <Option value="#943d73">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: '#943d73', borderRadius: '2px' }} />
                  Dark Pink
                </div>
              </Option>
              <Option value="#5e6c84">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '16px', height: '16px', backgroundColor: '#5e6c84', borderRadius: '2px' }} />
                  Gray
                </div>
              </Option>
            </Select>
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'end', gap: 8, marginTop: 24 }}>
            <Button onClick={() => setIsStatusModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingStatus ? 'Update' : 'Create'}
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Global Context Modal */}
      <Modal
        title={editingContext ? 'Edit Context Entry' : 'Add New Context Entry'}
        open={isContextModalOpen}
        onCancel={() => setIsContextModalOpen(false)}
        footer={null}
        width={700}
      >
        <Form
          form={contextForm}
          onFinish={handleContextSave}
          layout="vertical"
          onValuesChange={(changedValues, allValues) => {
            // Auto-update namespace when namespace type or client changes
            if (changedValues.namespace_type || changedValues.client_id) {
              const namespaceType = changedValues.namespace_type || allValues.namespace_type;
              const clientId = changedValues.client_id || allValues.client_id;
              
              let namespace = '';
              if (namespaceType === 'client' && clientId) {
                namespace = `client.${clientId}`;
              } else if (namespaceType === 'system') {
                namespace = 'system';
              } else if (namespaceType === 'workflow') {
                namespace = 'workflow.templates';
              } else if (namespaceType === 'custom') {
                namespace = allValues.custom_namespace || '';
              }
              
              if (namespace) {
                contextForm.setFieldsValue({ namespace });
              }
            }
          }}
        >
          {!editingContext && (
            <>
              <Form.Item
                name="namespace_type"
                label="Context Type"
                rules={[{ required: true, message: 'Please select context type' }]}
                initialValue="client"
              >
                <Select placeholder="Select context type">
                  <Option value="client">Client Settings</Option>
                  <Option value="system">System Settings</Option>
                  <Option value="workflow">Workflow Templates</Option>
                  <Option value="custom">Custom Namespace</Option>
                </Select>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => 
                  prevValues.namespace_type !== currentValues.namespace_type
                }
              >
                {({ getFieldValue }) => {
                  const namespaceType = getFieldValue('namespace_type');
                  
                  if (namespaceType === 'client') {
                    return (
                      <Form.Item
                        name="client_id"
                        label="Select Client"
                        rules={[{ required: true, message: 'Please select a client' }]}
                      >
                        <Select placeholder="Choose client">
                          {clients.map(client => (
                            <Option key={client.id} value={client.id}>
                              {client.name} ({client.contact_email || client.email})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    );
                  }
                  
                  if (namespaceType === 'custom') {
                    return (
                      <Form.Item
                        name="custom_namespace"
                        label="Custom Namespace"
                        rules={[{ required: true, message: 'Please enter custom namespace' }]}
                      >
                        <Input placeholder="e.g., myapp.settings, integration.config" />
                      </Form.Item>
                    );
                  }
                  
                  return null;
                }}
              </Form.Item>
            </>
          )}

          <Form.Item
            name="namespace"
            label="Generated Namespace"
            rules={[{ required: true, message: 'Please enter namespace' }]}
          >
            <Input 
              placeholder="Namespace will be auto-generated based on your selection above" 
              disabled={!editingContext}
              style={{ backgroundColor: editingContext ? undefined : '#f5f5f5' }}
            />
          </Form.Item>

          <Form.Item
            name="context_key"
            label="Context Key"
            rules={[{ required: true, message: 'Please enter context key' }]}
          >
            <Input 
              placeholder="e.g., jobprefix, storage_path, notification_email" 
              disabled={!!editingContext}
            />
          </Form.Item>

          <Form.Item
            name="context_value"
            label="Context Value"
            rules={[{ required: true, message: 'Please enter context value' }]}
          >
            <TextArea 
              placeholder="e.g., client1_, /clients/client1, user@example.com"
              rows={3}
            />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            initialValue="General"
          >
            <Select placeholder="Select category">
              <Option value="General">General</Option>
              <Option value="Workflow">Workflow</Option>
              <Option value="Storage">Storage</Option>
              <Option value="Notifications">Notifications</Option>
              <Option value="Performance">Performance</Option>
              <Option value="Templates">Templates</Option>
              <Option value="System">System</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea 
              placeholder="Description of this context entry..."
              rows={2}
            />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'end', gap: 8, marginTop: 24 }}>
            <Button onClick={() => setIsContextModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {editingContext ? 'Update' : 'Create'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Settings;
